import React, {useState} from 'react';
import Papa from 'papaparse';
import {toast} from 'react-toastify';
import api from '../Interceptor';
import {getRoute} from '../api/routes';
import CustomModal from './customModal/customModal';
import useModal from '../hooks/useModal';

const ALLOWED_CURRENCIES = ['USD', 'PKR', 'SAR', 'GBP', 'EUR', 'AED'];
const REQUIRED_HEADERS = [
  'ProjectName',
  'ProjectCurrency',
  'MilestoneName',
  'MilestoneStartDate',
  'MilestoneEndDate',
  'ExpenseName',
  'ExpenseDescription',
  'ExpenseAmount',
  'ExpenseType',
  'ExpenseConversionRate',
  'ExpenseDate',
];

const validateDate = (dateStr) => {
  if (typeof dateStr !== 'string') return null;

  // Check for MM/DD/YYYY format first
  const dateRegex = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
  const match = dateStr.match(dateRegex);

  if (match) {
    let [, month, day, year] = match.map(Number);
    if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1000)
      return null;
    const formattedDate = new Date(year, month - 1, day);
    return isNaN(formattedDate.getTime())
      ? null
      : formattedDate.toISOString().split('T')[0];
  }

  // Check for alternative format (YYYY-MM-DD)
  if (!isNaN(Date.parse(dateStr))) {
    return new Date(dateStr).toISOString().split('T')[0];
  }

  return null;
};

const validateCSVFormat = (data) => {
  if (!data.length) {
    toast.error('CSV file is empty.');
    return false;
  }
  const headers = Object.keys(data[0]);
  if (!REQUIRED_HEADERS.every((header) => headers.includes(header))) {
    toast.error('CSV headers are incorrect. Please check the file format.');
    return false;
  }
  for (const row of data) {
    if (!ALLOWED_CURRENCIES.includes(row.ProjectCurrency)) {
      toast.error(`Invalid currency: ${row.ProjectCurrency}`);
      return false;
    }
    if (!['income', 'expense'].includes(row.ExpenseType.toLowerCase())) {
      toast.error(`Invalid ExpenseType: ${row.ExpenseType}`);
      return false;
    }
    if (
      !validateDate(row.MilestoneStartDate) ||
      // !validateDate(row.MilestoneEndDate) ||
      !validateDate(row.ExpenseDate)
    ) {
      toast.error('Invalid date format. Use MM/DD/YYYY.');
      console.log(
        'Date Format:',
        row.MilestoneStartDate,
        row.MilestoneEndDate,
        row.ExpenseDate
      );
      return false;
    }
  }
  return true;
};

const parseCSVToJSON = (file, callback) => {
  Papa.parse(file, {
    header: true,
    skipEmptyLines: true,
    complete: (result) => {
      if (!validateCSVFormat(result.data)) {
        return;
      }
      const projects = {};
      result.data.forEach((row) => {
        const projectKey = `${row.ProjectName}-${row.ProjectCurrency}`;
        const milestoneKey = `${row.MilestoneName}-${row.MilestoneStartDate}-${row.MilestoneEndDate}`;
        if (!projects[projectKey]) {
          projects[projectKey] = {
            name: row.ProjectName,
            currency: row.ProjectCurrency,
            milestones: {},
          };
        }
        if (!projects[projectKey].milestones[milestoneKey]) {
          projects[projectKey].milestones[milestoneKey] = {
            name: row.MilestoneName,
            startDate: validateDate(row.MilestoneStartDate),
            endDate: validateDate(row.MilestoneEndDate) || undefined || null,
            expenses: [],
          };
        }
        projects[projectKey].milestones[milestoneKey].expenses.push({
          name: row.ExpenseName,
          description: row.ExpenseDescription,
          amount: parseFloat(row.ExpenseAmount),
          conversionRate: parseFloat(row.ExpenseConversionRate),
          type: row.ExpenseType.toLowerCase(),
          date: validateDate(row.ExpenseDate),
        });
      });
      const finalJSON = {
        projects: Object.values(projects).map((proj) => ({
          ...proj,
          milestones: Object.values(proj.milestones),
        })),
      };
      callback(finalJSON);
    },
    error: (err) => {
      toast.error('Error parsing CSV: ' + err.message);
    },
  });
};

const ImportBulkExpenses = () => {
  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isFileRead, setIsFileRead] = useState(false);
  const {isOpen, toggleModal} = useModal();
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) {
      toast.error('Please select a file.');
      return;
    }
    setFile(selectedFile);
    setParsedData(null);
    setIsFileRead(false);
  };

  const handleReadFile = () => {
    if (!file) {
      toast.error('Please select a file first.');
      return;
    }
    parseCSVToJSON(file, (data) => {
      setParsedData(data);
      setIsFileRead(true);
      toast.success('File read successfully. Now you can upload.');
    });
  };

  const handleUpload = async () => {
    if (!parsedData) {
      toast.error('No data to upload.');
      return;
    }
    setLoading(true);
    try {
      const response = await api.post(
        getRoute('users', 'admin', 'bulkImport', false),
        parsedData
      );
      window.location.href = window.location.href;
      toast.success('Expenses uploaded successfully.');
      toggleModal();
    } catch (error) {
      toast.error('Error uploading expenses.');
    }
    setLoading(false);
  };

  const showModal = () => {
    toggleModal();
    setFile(null);
    setParsedData(null);
    setIsFileRead(false);
  };
  return (
    <div>
      {!isFileRead && (
        <button
          className="bg-redGradient text-white w-full md:w-auto px-2 py-2 rounded-md md:mb-0 flex items-center justify-center"
          onClick={showModal}
        >
          <i className="fa-solid fa-download text-md"></i>
          <span className="ms-2">Bulk Import</span>
        </button>
      )}
      <CustomModal
        modalTitle="Import Bulk Expenses"
        isOpen={isOpen}
        onClose={toggleModal}
      >
        <div className="flex flex-col space-y-3">
          {/* Info + Sample CSV */}
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-md text-sm text-black">
            Only <strong className="text-red-500">.csv</strong> files are
            allowed. Need a reference?
            <a
              href="/samples/Bulk_Import_Sample.csv" // <-- your sample CSV path
              download="expenses-sample.csv"
              className="text-blue-600 underline ml-1 hover:text-blue-800"
            >
              Download sample CSV
            </a>
          </div>

          <input type="file" accept=".csv" onChange={handleFileChange} />

          {/* Read File Button */}
          {!isFileRead && (
            <button
              onClick={handleReadFile}
              disabled={!file}
              className={`${
                file
                  ? 'bg-themeGradient text-white'
                  : 'bg-gray-400 text-gray-200'
              } rounded-lg px-3 py-2`}
            >
              Read File
            </button>
          )}

          {/* Upload Button */}
          {isFileRead && (
            <button
              onClick={handleUpload}
              disabled={!parsedData || loading}
              className={`${
                loading ? 'bg-gray-400' : 'bg-themeGradient'
              } text-white rounded-lg px-3 py-2`}
            >
              {loading ? 'Uploading...' : 'Upload'}
            </button>
          )}
        </div>
      </CustomModal>
    </div>
  );
};

export default ImportBulkExpenses;
